import { useMemo } from "react";
import { useQuery } from "@pos/shared/query";
import { fetchReportConfig } from "@pos/shared/api";
import { useReportAccess } from "@pos/shared";
import type {
  ReportConfig,
  ParsedReportGroup,
  ParsedReportEntry,
} from "@pos/shared/types";

/**
 * Loads the report config JSON via TanStack Query, filters by user
 * permissions, and returns grouped reports ready for the sidebar.
 */
export function useReportConfig() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["reports", "config"],
    queryFn: fetchReportConfig,
    staleTime: 1000 * 60 * 30,
  });

  const { reports: userReports, hasReport } = useReportAccess();

  const groups = useMemo((): ParsedReportGroup[] => {
    const config: ReportConfig | undefined = data;
    if (!config) return [];

    // If user has no explicit report permissions, show all reports
    const showAll = userReports.length === 0;

    const result: ParsedReportGroup[] = [];

    for (const [groupKey, group] of Object.entries(config)) {
      const reports: ParsedReportEntry[] = [];

      for (const [reportKey, definition] of Object.entries(group.reports)) {
        if (
          showAll ||
          hasReport(reportKey) ||
          hasReport(String(definition.report_id))
        ) {
          reports.push({ key: reportKey, definition });
        }
      }

      if (reports.length > 0) {
        result.push({ groupKey, name: group.name, reports });
      }
    }

    return result;
  }, [data, userReports, hasReport]);

  return { groups, isLoading, isError };
}
